// noinspection SpellCheckingInspection

import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import { styled } from "@mui/material/styles";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import { MaskedTeam, Masked } from "work/masked-fields";
import type Guid from "_shared/guids/guid";

const AudienceLabel = styled("span")<{ isexternal: boolean; disabled?: boolean }>(
  ({ theme, isexternal, disabled }) => ({
    color: isexternal ? theme.palette.error.main : theme.palette.common.black,
    fontWeight: isexternal ? theme.typography.fontWeightBold : undefined,
    opacity: disabled ? 0.5 : 1,
    // Two words that mean one thing. Left to wrap, the label breaks itself across two lines rather
    // than letting the row it sits in wrap, which is the wrong half of the layout to give way.
    whiteSpace: "nowrap",
  })
);
/**
 * Rendered as a `span` (hence the `as` at the usage), not the `p` a `FormHelperText` defaults to. This
 * switch lives in the comment composer's own `helperText` — already a `p` — and its note carries a
 * block of its own, so the default gave React a `p` inside a `p` inside a `p` and it logged a hydration
 * error on every toggle. Same reason `comment-item.tsx` overrides its `ListItemText` slots.
 */
const HelperText = styled(FormHelperText)(() => ({
  display: "block",
  textWrap: "wrap"
}));
const AudienceNote = styled("span")(({ theme }) => ({
  display: "block",
  marginTop: theme.spacing(1),
}));

/**
 * Whether a user may write to all stakeholders: the client team's leader, or the vendor team's.
 *
 * A reviewer reads the all-stakeholders conversation and writes only in their own team's. The pane's
 * all-stakeholders tab and this switch both ask this, so they agree on who may speak for a party.
 */
export function canAddressCounterparty(proposal: Proposal | undefined, userId: Guid | undefined): boolean {
  if (!proposal) return true;
  const isClient =
    MaskedTeam.isPresentNotMasked(proposal.workAgreement.clientTeam) &&
    !!proposal.workAgreement.clientTeam?.leader?.userId.isEqualTo(userId);
  const isTeamLeader =
    Masked.isPresentNotMasked(proposal.vendorTeam) && !!proposal.vendorTeam.leader?.userId.isEqualTo(userId);
  return isClient || isTeamLeader;
}

/**
 * Whether a user reviews the proposal, for either party, and leads neither. Such a reviewer reads the
 * all-stakeholders conversation and posts nothing in it: the server refuses the post, and leaves them
 * off its member list, because the other party must not learn who a party's reviewers are (#6623).
 */
export function onlyReviews(proposal: Proposal | undefined, userId: Guid | undefined): boolean {
  if (!proposal || !userId) return false;
  const reviews = [...proposal.clientReviewers, ...proposal.vendorReviewers].some((reviewer) =>
    reviewer.userId.isEqualTo(userId),
  );
  return reviews && !canAddressCounterparty(proposal, userId);
}

type AudienceSwitchProps = {
  className?: string;
  proposal: Proposal;
  disabled?: boolean;
  isExternal?: boolean;
  onIsExternalChanged: (isExternal: boolean) => void;
};

export default function AudienceSwitch(props: Readonly<AudienceSwitchProps>) {
  const { disabled, proposal, onIsExternalChanged } = props;

  const session = useSession();

  const isExternal = props.isExternal ?? false;

  const mayAddressCounterparty = canAddressCounterparty(proposal, session.user?.id);

  function handleIsExternalChanged() {
    onIsExternalChanged(!isExternal);
  }

  return (
    <Tooltip title={disabled ? "You cannot change a comment from external to internal" : ""}>
      <span>
        <FormControl component="span" variant="standard" size="small" disabled={disabled || !mayAddressCounterparty}>
          <FormControlLabel
            control={
              <Switch
                checked={isExternal}
                disabled={disabled || !mayAddressCounterparty}
                color={isExternal ? "error" : undefined}
                onChange={handleIsExternalChanged}
              />
            }
            label={
              <AudienceLabel 
                isexternal={isExternal} 
                disabled={disabled || !mayAddressCounterparty}>
                External Comment
              </AudienceLabel>
            }
          />
          {isExternal && (
            <HelperText as="span">
              <strong>Note:</strong> This comment will be visible to everyone associated with this proposal, including:
              <AudienceNote>
                <i>the proposal creator, team, and both client and vendor reviewers</i>
              </AudienceNote>
            </HelperText>
          )}
        </FormControl>
      </span>
    </Tooltip>
  );
}
